import {createStore} from '@ngneat/elf';
import {
  withEntities,
  setEntities,
  addEntities,
  updateEntities,
  withActiveId,
  selectActiveEntity,
  setActiveId,
  selectAllEntities
} from '@ngneat/elf-entities';import {withRequestsCache, withRequestsStatus} from '@ngneat/elf-requests';
import {Injectable} from "@angular/core";
import {Observable, of} from 'rxjs';
import {catchError, distinctUntilChanged, map, shareReplay, switchMap, tap} from 'rxjs/operators';
import {User} from "../models/user.model";
import {RestService} from '../../../shared/services/rest.service';

const storeName = 'users';

export const store = createStore(
  {name: storeName},
  withEntities<User>(),
  withActiveId(),
  withRequestsCache<typeof storeName>(),
  withRequestsStatus<typeof storeName>()
);

@Injectable({providedIn: 'root'})
export class UserRepository {
  private readonly _userNameRequests = new Map<number, Observable<string | null>>();

  constructor(
    private readonly _restService: RestService
  ) {
  }

  addUser(user: User) {
    store.update(addEntities(user));
  }

  loadAllUsers(): Observable<User[]> {
    return this._restService.httpGet('/users/all').pipe(
      tap((users: User[]) => {
        if (users?.length) {
          store.update(setEntities(users));
        }
      }),
      catchError(() => of([]))
    );
  }

  loadUsersByShelter(shelterId: number): Observable<User[]> {
    return this._restService.httpGet(`/users/all?animalShelter.id=${shelterId}`).pipe(
      map((users: User[]) => users ?? []),
      catchError(() => of([]))
    );
  }

  getAllUsers(): Observable<User[]> {
    return store.pipe(selectAllEntities());
  }

  updateUser(id: User['id'], user: Partial<User>) {
    store.update(updateEntities(id, user));
  }

  getActiveUser() {
    return store.pipe(selectActiveEntity());
  }

  getUserNameById(id: User['id'] | string) {
    const normalizedId = Number(id);
    if (Number.isNaN(normalizedId)) {
      return of(null);
    }

    return store.pipe(
      selectAllEntities(),
      map(users => users.find(user => Number(user.id) === normalizedId)?.name ?? null),
      switchMap(userName => userName ? of(userName) : this.fetchUserNameById(normalizedId)),
      distinctUntilChanged()
    );
  }

  private fetchUserNameById(id: number): Observable<string | null> {
    const cachedRequest = this._userNameRequests.get(id);
    if (cachedRequest) {
      return cachedRequest;
    }

    const request$ = this._restService.httpGet(`/users/${id}`).pipe(
      map((user: any) => user?.displayName ?? user?.name ?? null),
      tap((name) => {
        if (!name) {
          return;
        }

        this.addUser({
          id,
          name,
          token: '',
          roles: [],
          defaultAnimalShelter: null
        });
      }),
      catchError(() => of(null)),
      shareReplay(1)
    );

    this._userNameRequests.set(id, request$);
    return request$;
  }

  setActiveUserId(id: User['id']) {
    store.update(setActiveId(id));
  }

  updateDisplayName(id: number, displayName: string): Observable<any> {
    return this._restService.httpPut(`/users/${id}`, {displayName}).pipe(
      tap(() => {
        store.update(updateEntities(id, {displayName}));
      })
    );
  }

  removeAllUsers() {
    store.update(setEntities([]));
  }
}
